import type { CSSProperties, ReactElement } from 'react';
import { Fragment } from 'react';
import { Panel } from '@xyflow/react';
import { Cbutton } from '@/components/ui/Cbutton';
import { ButtonPreset } from '@/constants/componentPresets';
import { getFontFamily } from '@/theme/typography';
import { findTreemapNodeById } from './layout-treemap';
import type { ParsedTreemap } from './treemap-types';
const PANEL_POSITION_TOP_LEFT = 'top-left';
const OVERVIEW_CRUMB_LABEL = 'Overview';
const BREADCRUMB_SEPARATOR = '\u203a';
const BREADCRUMB_BAR_STYLE: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
    maxWidth: 'calc(100% - 24px)',
    padding: '5px 9px',
    borderRadius: 8,
    backgroundColor: 'rgba(10, 14, 22, 0.82)',
    border: '1px solid rgba(255,255,255,0.08)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
};
const BREADCRUMB_CRUMB_STYLE: CSSProperties = {
    fontFamily: getFontFamily(),
    fontSize: 11,
    color: 'rgba(255,255,255,0.6)',
    padding: '2px 4px',
    borderRadius: 4,
    maxWidth: 160,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
};
const BREADCRUMB_CURRENT_STYLE: CSSProperties = {
    color: '#66FEC1',
    cursor: 'default',
};
const BREADCRUMB_SEPARATOR_STYLE: CSSProperties = {
    color: 'rgba(255,255,255,0.3)',
    fontSize: 11,
};
interface TreemapBreadcrumbProps {
    parsed: ParsedTreemap | null;
    drillPathIds: string[];
    onDrillToDepth: (index: number) => void;
}
export function TreemapBreadcrumb({ parsed, drillPathIds, onDrillToDepth, }: TreemapBreadcrumbProps): ReactElement | null {
    if (!parsed || (!parsed.title && drillPathIds.length === 0)) {
        return null;
    }
    return (<Panel position={PANEL_POSITION_TOP_LEFT}>
      <div data-el="Cmarkdowntreemap/TreemapBreadcrumb/Root" style={BREADCRUMB_BAR_STYLE}>
        <Cbutton preset={ButtonPreset.Ghost} disableHoverEffects ariaLabel={`Return to ${parsed.title ?? OVERVIEW_CRUMB_LABEL}`} padding={0} radius={4} onClick={() => onDrillToDepth(-1)} style={{ maxWidth: 160, cursor: 'pointer' }} containerStyle={BREADCRUMB_CRUMB_STYLE}>
          {parsed.title ?? OVERVIEW_CRUMB_LABEL}
        </Cbutton>
        {drillPathIds.map((nodeId, index) => {
            const crumbNode = findTreemapNodeById({ root: parsed.root, nodeId });
            const isLast = index === drillPathIds.length - 1;
            const crumbIdentity = crumbNode?.label ?? nodeId;
            return (<Fragment key={nodeId}>
              <span style={BREADCRUMB_SEPARATOR_STYLE}>
                {BREADCRUMB_SEPARATOR}
              </span>
              <Cbutton preset={ButtonPreset.Ghost} disableHoverEffects ariaLabel={`Drill to ${crumbIdentity}`} padding={0} radius={4} style={{ maxWidth: 160, cursor: isLast ? 'default' : 'pointer' }} containerStyle={isLast ? { ...BREADCRUMB_CRUMB_STYLE, ...BREADCRUMB_CURRENT_STYLE } : BREADCRUMB_CRUMB_STYLE} onClick={() => onDrillToDepth(index)} disabled={isLast}>
                {crumbNode?.label ?? nodeId}
              </Cbutton>
            </Fragment>);
        })}
      </div>
    </Panel>);
}
